import { useLoggedInQuery } from '@/src/common/gql/MesaGql'
import { formatToMonthYear, sortByTimeDescending } from '@/src/common/utils/date'
import { Queries } from '@/src/experiences/notifications/gql'
import {
  UserPushNotificationStatus,
  type GetUserNotificationsQuery,
  type GetUserNotificationsQueryVariables,
} from '@api/mesa/graphql'
import { useMemo, useState } from 'react'
import { useLogError } from '@/src/common/hooks/useLogError'
import { useEvent } from 'tamagui'

type UserNotification = GetUserNotificationsQuery['getUserNotifications']['notifications'][number]

/** dedupes, dates, and sorts user notifications */
function getDatedNotifications(notifications: UserNotification[]) {
  return Object.values(
    notifications.reduce<Record<UserNotification['id'], UserNotification>>((acc, notification) => {
      acc[notification.id] = notification
      return acc
    }, {}),
  )
    .map((notification) => {
      /**
       * the old version of the response would unnecessarily convert the Date into a timestamp string instead of using the
       * native iso datestring conversion. rather than create a whole new versioned query, let's make the breaking api change
       * but ensure the clients can support both versions, which we can get away with doing because of over the air updates.
       */
      const legacyDate = Number(notification.createdAt)
      return {
        notification,
        date: isNaN(legacyDate) ? new Date(notification.createdAt) : new Date(legacyDate),
      } as const
    })
    .toSorted(({ date: a }, { date: b }) => b.getTime() - a.getTime())
}

export const NOTIFICATION_FILTERS = ['All', 'Received', 'Opened'] as const

type NotificationFilter = (typeof NOTIFICATION_FILTERS)[number]

export function useUserNotifications() {
  const onError = useLogError()

  const [selectedFilter, setSelectedFilter] = useState<NotificationFilter>(NOTIFICATION_FILTERS[0])

  const variables = useMemo(() => {
    switch (selectedFilter) {
      case 'Opened':
        return { pushNotificationStatus: UserPushNotificationStatus.Opened }
      case 'Received':
        return { pushNotificationStatus: UserPushNotificationStatus.Received }
      default:
        return undefined
    }
  }, [selectedFilter]) satisfies GetUserNotificationsQueryVariables | undefined

  const { data, loading, fetchMore, refetch } = useLoggedInQuery(Queries.GET_USER_NOTIFICATIONS, {
    fetchPolicy: 'cache-and-network',
    variables,
    onError,
  })

  const oldestNotification = data?.getUserNotifications.notifications.at(-1)

  const fetchMoreNotifications = useEvent(() =>
    fetchMore({
      variables: {
        ...variables,
        ...(oldestNotification ? { cursor: { id: oldestNotification.id } } : undefined),
      },
      updateQuery: (previous, { fetchMoreResult }) => ({
        ...fetchMoreResult,
        getUserNotifications: {
          ...fetchMoreResult.getUserNotifications,
          notifications: getDatedNotifications([
            // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
            ...(previous.getUserNotifications?.notifications || []),
            ...fetchMoreResult.getUserNotifications.notifications,
          ]).map(({ notification }) => notification),
        },
      }),
    }),
  )

  const userNotifications = getDatedNotifications(data?.getUserNotifications.notifications ?? [])

  const sections = Object.entries(
    Object.groupBy(userNotifications, ({ date }) => formatToMonthYear(date)),
  )
    .toSorted(([monthA], [monthB]) => sortByTimeDescending(monthA, monthB))
    .flatMap(([title, data]) => (data?.length ? ({ title, data } as const) : []))

  return {
    selectedFilter,
    setSelectedFilter,
    sections,
    loading,
    refetch,
    fetchMoreNotifications,
  } as const
}

export type DatedUserNotification = ReturnType<
  typeof useUserNotifications
>['sections'][number]['data'][number]
